import { useEffect, useState, useCallback } from "react";
import { X as XIcon, ChevronLeft, ChevronRight } from "lucide-react";

/**
 * ImageViewerModal — 1:1 web port of iOS
 * `mobile/src/components/ImageViewerModal.tsx`.
 *
 * Near-black backdrop (rgba(0,0,0,0.96)), image at up to 92vw × 80vh
 * with `object-contain` so it never crops or letterboxes.  An X button
 * closes; multi-image posts get a "n / total" counter at the top and
 * left/right chevron arrows for stepping between images.  Tapping the
 * backdrop also dismisses.  Left/Right arrow keys navigate; Escape closes.
 *
 * Zoom: click-to-toggle 2× zoom on the image itself (works well on both
 * touch and desktop; native pinch works when zoomed).
 */
export default function ImageViewerModal({
  urls,
  open,
  initialIndex = 0,
  onClose,
}) {
  const [i, setI] = useState(initialIndex);
  const [zoomed, setZoomed] = useState(false);

  useEffect(() => {
    if (open) {
      setI(initialIndex);
      setZoomed(false);
    }
  }, [open, initialIndex]);

  const next = useCallback(() => {
    setZoomed(false);
    setI((v) => (urls.length ? (v + 1) % urls.length : 0));
  }, [urls.length]);

  const prev = useCallback(() => {
    setZoomed(false);
    setI((v) => (urls.length ? (v - 1 + urls.length) % urls.length : 0));
  }, [urls.length]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
      else if (e.key === "ArrowRight") next();
      else if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    // Prevent body scroll while viewer is open.
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose, next, prev]);

  if (!open || !urls || urls.length === 0) return null;
  const src = urls[Math.min(i, urls.length - 1)];
  const multi = urls.length > 1;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center"
      style={{ background: "rgba(0,0,0,0.96)" }}
      onClick={onClose}
      data-testid="image-viewer"
      role="dialog"
      aria-modal="true"
    >
      {/* Close button */}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onClose?.();
        }}
        className="absolute top-4 right-4 sm:top-6 sm:right-6 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors"
        aria-label="Close image viewer"
        data-testid="image-viewer-close"
      >
        <XIcon size={20} />
      </button>

      {/* Counter — n / total */}
      {multi && (
        <div
          className="absolute top-5 sm:top-7 left-1/2 -translate-x-1/2 text-white text-sm font-semibold tabular-nums bg-black/40 rounded-full px-3 py-1 pointer-events-none"
          data-testid="image-viewer-counter"
        >
          {i + 1} / {urls.length}
        </div>
      )}

      {/* Previous chevron */}
      {multi && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            prev();
          }}
          className="absolute left-3 sm:left-6 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors"
          aria-label="Previous image"
          data-testid="image-viewer-prev"
        >
          <ChevronLeft size={26} />
        </button>
      )}

      {/* Next chevron */}
      {multi && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            next();
          }}
          className="absolute right-3 sm:right-6 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors"
          aria-label="Next image"
          data-testid="image-viewer-next"
        >
          <ChevronRight size={26} />
        </button>
      )}

      {/* The image itself — click toggles 2× zoom, click on backdrop closes */}
      <img
        src={src}
        alt=""
        onClick={(e) => {
          e.stopPropagation();
          setZoomed((z) => !z);
        }}
        className="select-none cursor-zoom-in transition-transform duration-200"
        draggable={false}
        style={{
          maxWidth: "92vw",
          maxHeight: "80vh",
          objectFit: "contain",
          transform: zoomed ? "scale(2)" : "scale(1)",
          transformOrigin: "center",
          cursor: zoomed ? "zoom-out" : "zoom-in",
        }}
        data-testid={`image-viewer-image-${i}`}
      />
    </div>
  );
}
